/**
 * Aplica um patch de sugestão no estado da config.
 * Retorna { ok, next, alreadyApplied? } ou { ok:false, reason }.
 */
export function applyConfigPatch(state, patch) {
    if (!patch || typeof patch.target !== "string") {
        return { ok: false, reason: "Patch inválido" };
    }

    const before = typeof patch.before === "string" ? patch.before : "";
    const after = typeof patch.after === "string" ? patch.after : "";
    const target = patch.target;

    if (target === "system_prompt" || target === "guardrails" || target === "modelo") {
        const key =
            target === "system_prompt"
                ? "systemPrompt"
                : target === "guardrails"
                  ? "guardrails"
                  : "modelo";
        const current = state[key] ?? "";
        const replaced = replaceFlexible(current, before, after);
        if (!replaced.ok) {
            return {
                ok: false,
                reason:
                    replaced.reason ||
                    `Não achei o trecho “before” em ${target} (já foi editado?).`,
            };
        }
        return {
            ok: true,
            alreadyApplied: replaced.alreadyApplied === true,
            next: { ...state, [key]: replaced.value },
        };
    }

    if (target === "temperatura") {
        const current = state.temperatura == null ? "" : String(state.temperatura);
        const replaced = replaceFlexible(current, String(before), String(after));
        if (!replaced.ok) {
            return {
                ok: false,
                reason: replaced.reason || "Não achei o trecho “before” em temperatura.",
            };
        }
        return {
            ok: true,
            alreadyApplied: replaced.alreadyApplied === true,
            next: { ...state, temperatura: replaced.value },
        };
    }

    if (target.startsWith("knowledge:")) {
        const filename = target.slice("knowledge:".length);
        const knowledge = Array.isArray(state.knowledge) ? [...state.knowledge] : [];
        const idx = knowledge.findIndex((k) => (k.filename || "") === filename);

        if (idx < 0) {
            if (before.trim() !== "") {
                return {
                    ok: false,
                    reason: `Arquivo ${filename} não existe no editor.`,
                };
            }
            knowledge.push({ filename, content: after });
            return { ok: true, next: { ...state, knowledge } };
        }

        const current = knowledge[idx].content ?? "";
        const replaced = replaceFlexible(current, before, after);
        if (!replaced.ok) {
            return {
                ok: false,
                reason:
                    replaced.reason || `Não achei o trecho “before” em ${filename}.`,
            };
        }
        knowledge[idx] = { ...knowledge[idx], content: replaced.value };
        return {
            ok: true,
            alreadyApplied: replaced.alreadyApplied === true,
            next: { ...state, knowledge },
        };
    }

    return { ok: false, reason: `Alvo não suportado: ${target}` };
}

/** Conta ocorrências não sobrepostas de `needle` em `haystack`. */
export function countOccurrences(haystack, needle) {
    if (needle === "") return haystack === "" ? 1 : 0;
    return haystack.split(needle).length - 1;
}

/**
 * Colapsa whitespace (qualquer sequência → 1 espaço) e mapeia cada char
 * do texto colapsado para o índice original.
 */
export function collapseWsWithMap(s) {
    const indexMap = [];
    let text = "";
    const re = /\S+/g;
    let m;
    let first = true;
    while ((m = re.exec(s)) !== null) {
        if (!first) {
            // espaço sintético aponta para o 1º whitespace entre tokens
            indexMap.push(m.index > 0 ? m.index - 1 : m.index);
            text += " ";
        }
        first = false;
        for (let c = 0; c < m[0].length; c += 1) {
            indexMap.push(m.index + c);
            text += m[0][c];
        }
    }
    return { text, indexMap };
}

/** Localiza um único intervalo em `current` cujo texto colapsado = `before` colapsado. */
export function findCollapsedMatch(current, before) {
    const needle = String(before || "").replace(/\s+/g, " ").trim();
    if (!needle) return null;
    const hay = collapseWsWithMap(current);
    if (!hay.text) return null;

    const starts = [];
    let from = 0;
    while (from <= hay.text.length) {
        const idx = hay.text.indexOf(needle, from);
        if (idx < 0) break;
        starts.push(idx);
        from = idx + 1;
    }
    if (starts.length !== 1) return null;

    const startNorm = starts[0];
    const endNorm = startNorm + needle.length - 1;
    if (endNorm >= hay.indexMap.length) return null;
    return {
        start: hay.indexMap[startNorm],
        end: hay.indexMap[endNorm] + 1,
    };
}

/**
 * Substitui `before` por `after` com tolerância a whitespace/newlines.
 * @returns {{ ok:true, value:string, alreadyApplied?:boolean } | { ok:false, reason?:string }}
 */
export function replaceFlexible(current, before, after) {
    const cur = current ?? "";
    const bRaw = before ?? "";
    const aRaw = after ?? "";

    if (bRaw === cur) {
        return { ok: true, value: aRaw };
    }

    if (bRaw === "") {
        if (cur === "") return { ok: true, value: aRaw };
        return { ok: false, reason: "before vazio só vale para campo/arquivo novo." };
    }

    const candidates = uniqueStrings([
        bRaw,
        bRaw.trim(),
        bRaw.replace(/\r\n/g, "\n"),
        bRaw.trim().replace(/\r\n/g, "\n"),
        bRaw.replace(/\r\n/g, "\n").replace(/[ \t]+\n/g, "\n"),
        bRaw.replace(/\s+/g, " ").trim(),
    ]);

    for (const b of candidates) {
        if (!b) continue;
        for (const hay of uniqueStrings([cur, cur.replace(/\r\n/g, "\n")])) {
            const n = countOccurrences(hay, b);
            if (n === 1) {
                const next = hay.split(b).join(aRaw);
                return { ok: true, value: next };
            }
            if (n > 1) {
                return {
                    ok: false,
                    reason: "Trecho “before” aparece mais de uma vez — refine a sugestão.",
                };
            }
        }
    }

    const collapsed = findCollapsedMatch(cur, bRaw);
    if (collapsed) {
        return {
            ok: true,
            value: cur.slice(0, collapsed.start) + aRaw + cur.slice(collapsed.end),
        };
    }

    // Já aplicado: after está no texto e before (exato/trim) não.
    const afterTrim = aRaw.trim();
    if (
        afterTrim &&
        countOccurrences(cur, aRaw) === 1 &&
        countOccurrences(cur, bRaw) === 0 &&
        countOccurrences(cur, bRaw.trim()) === 0
    ) {
        return { ok: true, value: cur, alreadyApplied: true };
    }

    // after colapsado único também conta como já aplicado
    const afterCollapsed = findCollapsedMatch(cur, aRaw);
    const beforeCollapsed = findCollapsedMatch(cur, bRaw);
    if (afterCollapsed && !beforeCollapsed) {
        return { ok: true, value: cur, alreadyApplied: true };
    }

    return { ok: false };
}

function uniqueStrings(list) {
    const out = [];
    const seen = new Set();
    for (const item of list) {
        if (typeof item !== "string" || seen.has(item)) continue;
        seen.add(item);
        out.push(item);
    }
    return out;
}
